/**
 * Advanced Map Filter Component
 * Table-style filtering with field name, operator, and value similar to optimization results
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Stack,
  Divider,
  Alert,
  Switch,
  FormControlLabel,
  Badge,
} from '@mui/material';
import {
  FilterList,
  Clear,
  Add,
  Remove,
  ExpandMore,
  Visibility,
  VisibilityOff,
  DirectionsCar,
  LocationOn,
  LocalShipping,
  Route,
  Settings,
  Search,
} from '@mui/icons-material';

export interface AdvancedFilterRule {
  id: string;
  field: string;
  operator: 'equals' | 'contains' | 'startsWith' | 'endsWith' | 'gt' | 'lt' | 'gte' | 'lte' | 'between';
  value: any;
  value2?: any; // For 'between' operator
  enabled: boolean;
}

export interface LayerVisibility {
  showVehicles: boolean;
  showLocations: boolean;
  showRoutes: boolean;
  showJobs: boolean;
}

export interface AdvancedMapFilterProps {
  // Filter rules
  filterRules: AdvancedFilterRule[];
  onFilterRulesChange: (rules: AdvancedFilterRule[]) => void;
  
  // Layer visibility
  layerVisibility: LayerVisibility;
  onLayerVisibilityChange: (visibility: LayerVisibility) => void;
  
  // Clustering control
  clusteringEnabled?: boolean;
  onClusteringChange?: (enabled: boolean) => void;
  
  // Data for building filter options
  vehicles?: any[];
  locations?: any[];
  jobs?: any[];
  routes?: any[];
  
  // Statistics
  totalVehicles?: number;
  totalLocations?: number;
  totalJobs?: number;
  totalRoutes?: number;
  
  // Layout props
  width?: number;
  height?: string;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

// Available fields for each entity type
const VEHICLE_FIELDS = [
  { value: 'description', label: 'Description', type: 'string' },
  { value: 'profile', label: 'Profile', type: 'select', options: ['driving', 'cycling', 'foot'] },
  { value: 'capacity', label: 'Capacity', type: 'number' },
  { value: 'cost_fixed', label: 'Fixed Cost', type: 'number' },
  { value: 'cost_per_hour', label: 'Cost per Hour', type: 'number' },
  { value: 'max_distance', label: 'Max Distance', type: 'number' },
  { value: 'speed_factor', label: 'Speed Factor', type: 'number' },
];

const LOCATION_FIELDS = [
  { value: 'name', label: 'Name', type: 'string' },
  { value: 'address', label: 'Address', type: 'string' },
  { value: 'location_type', label: 'Type', type: 'select', options: ['depot', 'customer', 'service_point'] },
  { value: 'location_lat', label: 'Latitude', type: 'number' },
  { value: 'location_lon', label: 'Longitude', type: 'number' },
];

const JOB_FIELDS = [
  { value: 'description', label: 'Description', type: 'string' },
  { value: 'priority', label: 'Priority', type: 'number' },
  { value: 'service', label: 'Service Time', type: 'number' },
  { value: 'setup', label: 'Setup Time', type: 'number' },
  { value: 'location_lat', label: 'Latitude', type: 'number' },
  { value: 'location_lon', label: 'Longitude', type: 'number' },
];

const ROUTE_FIELDS = [
  { value: 'vehicle_id', label: 'Vehicle ID', type: 'number' },
  { value: 'step_type', label: 'Step Type', type: 'select', options: ['start', 'job', 'end'] },
  { value: 'step_order', label: 'Step Order', type: 'number' },
  { value: 'distance', label: 'Distance', type: 'number' },
  { value: 'duration', label: 'Duration', type: 'number' },
];

const OPERATORS = [
  { value: 'equals', label: 'Equals (=)', types: ['string', 'number', 'select'] },
  { value: 'contains', label: 'Contains', types: ['string'] },
  { value: 'startsWith', label: 'Starts with', types: ['string'] },
  { value: 'endsWith', label: 'Ends with', types: ['string'] },
  { value: 'gt', label: 'Greater than (>)', types: ['number'] },
  { value: 'lt', label: 'Less than (<)', types: ['number'] },
  { value: 'gte', label: 'Greater or equal (>=)', types: ['number'] },
  { value: 'lte', label: 'Less or equal (<=)', types: ['number'] },
  { value: 'between', label: 'Between', types: ['number'] },
];

export const AdvancedMapFilter: React.FC<AdvancedMapFilterProps> = ({
  filterRules,
  onFilterRulesChange,
  layerVisibility,
  onLayerVisibilityChange,
  clusteringEnabled = true,
  onClusteringChange,
  vehicles = [],
  locations = [],
  jobs = [],
  routes = [],
  totalVehicles = 0,
  totalLocations = 0,
  totalJobs = 0,
  totalRoutes = 0,
  width = 320,
  height = 'calc(100vh - 140px)',
  expanded: expandedProp = true,
  onExpandedChange,
}) => {
  const [localExpanded, setLocalExpanded] = useState(true);
  const [activeSection, setActiveSection] = useState<string>('layers');

  const expanded = expandedProp !== undefined ? expandedProp : localExpanded;
  const setExpanded = onExpandedChange || setLocalExpanded;

  // Calculate active filters count
  const activeFiltersCount = useMemo(() => {
    return filterRules.filter(rule => rule.enabled).length;
  }, [filterRules]);

  // Add new filter rule
  const addFilterRule = useCallback(() => {
    const newRule: AdvancedFilterRule = {
      id: `filter_${Date.now()}`,
      field: 'description',
      operator: 'contains',
      value: '',
      enabled: true,
    };
    onFilterRulesChange([...filterRules, newRule]);
  }, [filterRules, onFilterRulesChange]);

  // Remove filter rule
  const removeFilterRule = useCallback((ruleId: string) => {
    onFilterRulesChange(filterRules.filter(rule => rule.id !== ruleId));
  }, [filterRules, onFilterRulesChange]);

  // Update filter rule
  const updateFilterRule = useCallback((ruleId: string, updates: Partial<AdvancedFilterRule>) => {
    onFilterRulesChange(filterRules.map(rule => 
      rule.id === ruleId ? { ...rule, ...updates } : rule
    ));
  }, [filterRules, onFilterRulesChange]);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    onFilterRulesChange([]);
  }, [onFilterRulesChange]);

  // Toggle layer visibility
  const toggleLayerVisibility = useCallback((layer: keyof LayerVisibility) => {
    onLayerVisibilityChange({
      ...layerVisibility,
      [layer]: !layerVisibility[layer],
    });
  }, [layerVisibility, onLayerVisibilityChange]);

  // Toggle clustering
  const toggleClustering = useCallback((enabled: boolean) => {
    onClusteringChange?.(enabled);
  }, [onClusteringChange]);

  // Get field options based on entity type
  const getFieldOptions = useCallback((entityType: string) => {
    switch (entityType) {
      case 'vehicles': return VEHICLE_FIELDS;
      case 'locations': return LOCATION_FIELDS;
      case 'jobs': return JOB_FIELDS;
      case 'routes': return ROUTE_FIELDS;
      default: return [];
    }
  }, []);

  // Get operators for field type
  const getOperatorsForField = useCallback((field: string, entityType: string) => {
    const fieldOptions = getFieldOptions(entityType);
    const fieldConfig = fieldOptions.find(f => f.value === field);
    const fieldType = fieldConfig?.type || 'string';
    
    return OPERATORS.filter(op => op.types.includes(fieldType));
  }, [getFieldOptions]);

  const getFilterStats = () => {
    const visibleLayers = [
      layerVisibility.showVehicles && 'Vehicles',
      layerVisibility.showLocations && 'Locations', 
      layerVisibility.showRoutes && 'Routes',
      layerVisibility.showJobs && 'Jobs'
    ].filter(Boolean);
    
    return {
      visibleLayers: visibleLayers.length,
      totalLayers: 4,
      layerNames: visibleLayers.join(', ')
    };
  };

  return (
    <Paper 
      elevation={3} 
      sx={{ 
        width,
        height,
        position: 'fixed',
        right: 16,
        top: 140,
        zIndex: 1000,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        border: '1px solid rgba(0,0,0,0.08)',
        transform: expanded ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.3s ease-in-out',
      }}
    >
      {/* Filter Header */}
      <Box sx={{ 
        p: 2, 
        borderBottom: '1px solid rgba(0,0,0,0.08)',
        bgcolor: 'primary.main',
        color: 'white',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Badge badgeContent={activeFiltersCount} color="secondary">
              <FilterList />
            </Badge>
            <Typography variant="h6" fontWeight={600}>
              Map Filters
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {activeFiltersCount > 0 && (
              <Tooltip title="Clear All Filters">
                <IconButton
                  size="small"
                  onClick={clearAllFilters}
                  sx={{ color: 'white' }}
                >
                  <Clear />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Close Filters">
              <IconButton
                size="small"
                onClick={() => setExpanded(false)}
                sx={{ color: 'white' }}
              >
                <ExpandMore />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
        {/* Quick Stats */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip 
            label={`${getFilterStats().visibleLayers}/${getFilterStats().totalLayers} layers`}
            size="small" 
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontSize: '0.75rem' }}
          />
          {activeFiltersCount > 0 && (
            <Chip 
              label={`${activeFiltersCount} filters active`}
              size="small" 
              color="secondary"
              sx={{ fontSize: '0.75rem' }}
            />
          )}
        </Box>
      </Box>

      {/* Filter Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
        
        {/* Layer Visibility Controls */}
        <Accordion 
          expanded={activeSection === 'layers'} 
          onChange={() => setActiveSection(activeSection === 'layers' ? '' : 'layers')}
          sx={{ mb: 1, '&:before': { display: 'none' }, boxShadow: 1 }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Visibility />
              <Typography fontWeight={600}>Layer Visibility</Typography>
              <Chip 
                label={`${getFilterStats().visibleLayers}/4`} 
                size="small" 
                color="primary"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <List dense>
              {[
                { key: 'showVehicles' as keyof LayerVisibility, icon: <DirectionsCar />, label: 'Vehicles', count: totalVehicles },
                { key: 'showLocations' as keyof LayerVisibility, icon: <LocationOn />, label: 'Locations', count: totalLocations },
                { key: 'showRoutes' as keyof LayerVisibility, icon: <Route />, label: 'Routes', count: totalRoutes },
                { key: 'showJobs' as keyof LayerVisibility, icon: <LocalShipping />, label: 'Jobs', count: totalJobs },
              ].map((layer) => {
                const isVisible = layerVisibility[layer.key];
                return (
                  <ListItem 
                    key={layer.key}
                    sx={{ 
                      px: 0, 
                      borderRadius: 1,
                      '&:hover': { bgcolor: 'grey.50' }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {layer.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={layer.label}
                      secondary={`${layer.count} items`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => toggleLayerVisibility(layer.key)}
                        color={isVisible ? 'primary' : 'default'}
                        size="small"
                      >
                        {isVisible ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
            
            {/* Clustering Control - Context-aware visibility */}
            {totalLocations > 10 && onClusteringChange && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ px: 2, pb: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={clusteringEnabled}
                        onChange={(e) => toggleClustering(e.target.checked)}
                        size="small"
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Route sx={{ fontSize: 18 }} />
                        <Typography variant="body2" fontWeight={500}>
                          Enable Clustering
                        </Typography>
                      </Box>
                    }
                    sx={{
                      m: 0,
                      '& .MuiFormControlLabel-label': {
                        color: 'text.primary',
                      },
                    }}
                  />
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.5, ml: 3 }}
                  >
                    Groups nearby locations ({totalLocations} total)
                  </Typography>
                </Box>
              </>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Advanced Filter Rules */}
        <Accordion 
          expanded={activeSection === 'rules'} 
          onChange={() => setActiveSection(activeSection === 'rules' ? '' : 'rules')}
          sx={{ mb: 1, '&:before': { display: 'none' }, boxShadow: 1 }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Settings />
              <Typography fontWeight={600}>Advanced Filters</Typography>
              {activeFiltersCount > 0 && (
                <Chip label={activeFiltersCount} size="small" color="primary" />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Stack spacing={2}>
              {/* Add Filter Button */}
              <Button
                startIcon={<Add />}
                onClick={addFilterRule}
                variant="outlined"
                size="small"
                fullWidth
              >
                Add Filter Rule
              </Button>

              {/* Filter Rules */}
              {filterRules.map((rule) => (
                <Paper key={rule.id} sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Stack spacing={2}>
                    {/* Rule Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={rule.enabled}
                            onChange={(e) => updateFilterRule(rule.id, { enabled: e.target.checked })}
                            size="small"
                          />
                        }
                        label="Enabled"
                      />
                      <IconButton
                        size="small"
                        onClick={() => removeFilterRule(rule.id)}
                        color="error"
                      >
                        <Remove />
                      </IconButton>
                    </Box>

                    {/* Entity Type */}
                    <FormControl fullWidth size="small">
                      <InputLabel>Entity Type</InputLabel>
                      <Select
                        value={rule.field.includes('vehicle') || VEHICLE_FIELDS.find(f => f.value === rule.field) ? 'vehicles' :
                               rule.field.includes('location') || LOCATION_FIELDS.find(f => f.value === rule.field) ? 'locations' :
                               rule.field.includes('job') || JOB_FIELDS.find(f => f.value === rule.field) ? 'jobs' : 'routes'}
                        onChange={(e) => {
                          const entityType = e.target.value;
                          const firstField = getFieldOptions(entityType)[0]?.value || 'description';
                          updateFilterRule(rule.id, { 
                            field: firstField,
                            operator: 'contains',
                            value: '',
                            value2: undefined
                          });
                        }}
                        label="Entity Type"
                      >
                        <MenuItem value="vehicles">🚗 Vehicles</MenuItem>
                        <MenuItem value="locations">📍 Locations</MenuItem>
                        <MenuItem value="jobs">📦 Jobs</MenuItem>
                        <MenuItem value="routes">🛣️ Routes</MenuItem>
                      </Select>
                    </FormControl>

                    {/* Field */}
                    <FormControl fullWidth size="small">
                      <InputLabel>Field</InputLabel>
                      <Select
                        value={rule.field}
                        onChange={(e) => updateFilterRule(rule.id, { 
                          field: e.target.value,
                          operator: 'contains',
                          value: '',
                          value2: undefined
                        })}
                        label="Field"
                      >
                        {getFieldOptions(
                          rule.field.includes('vehicle') || VEHICLE_FIELDS.find(f => f.value === rule.field) ? 'vehicles' :
                          rule.field.includes('location') || LOCATION_FIELDS.find(f => f.value === rule.field) ? 'locations' :
                          rule.field.includes('job') || JOB_FIELDS.find(f => f.value === rule.field) ? 'jobs' : 'routes'
                        ).map(field => (
                          <MenuItem key={field.value} value={field.value}>
                            {field.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Operator */}
                    <FormControl fullWidth size="small">
                      <InputLabel>Operator</InputLabel>
                      <Select
                        value={rule.operator}
                        onChange={(e) => updateFilterRule(rule.id, { 
                          operator: e.target.value as any,
                          value: '',
                          value2: undefined
                        })}
                        label="Operator"
                      >
                        {getOperatorsForField(rule.field, 
                          rule.field.includes('vehicle') || VEHICLE_FIELDS.find(f => f.value === rule.field) ? 'vehicles' :
                          rule.field.includes('location') || LOCATION_FIELDS.find(f => f.value === rule.field) ? 'locations' :
                          rule.field.includes('job') || JOB_FIELDS.find(f => f.value === rule.field) ? 'jobs' : 'routes'
                        ).map(op => (
                          <MenuItem key={op.value} value={op.value}>
                            {op.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Value(s) */}
                    <Stack spacing={1}>
                      <TextField
                        label="Value"
                        value={rule.value}
                        onChange={(e) => updateFilterRule(rule.id, { value: e.target.value })}
                        size="small"
                        fullWidth
                      />
                      {rule.operator === 'between' && (
                        <TextField
                          label="To Value"
                          value={rule.value2 || ''}
                          onChange={(e) => updateFilterRule(rule.id, { value2: e.target.value })}
                          size="small"
                          fullWidth
                        />
                      )}
                    </Stack>
                  </Stack>
                </Paper>
              ))}

              {filterRules.length === 0 && (
                <Alert severity="info">
                  <Typography variant="body2">
                    No filter rules defined. Click "Add Filter Rule" to create advanced filters.
                  </Typography>
                </Alert>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Filter Summary */}
        {activeFiltersCount > 0 && (
          <Alert 
            severity="info" 
            sx={{ mt: 2 }}
            action={
              <Button size="small" onClick={clearAllFilters}>
                Clear All
              </Button>
            }
          >
            <Typography variant="body2">
              {activeFiltersCount} filter rule{activeFiltersCount > 1 ? 's' : ''} active
            </Typography>
          </Alert>
        )}
      </Box>
    </Paper>
  );
};

export default AdvancedMapFilter;